import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  PROPERTY_TYPES, BOOKING_MODES, formatMoney, DEFAULT_AMENITIES, orderedCategories,
  type HebergementProperty, type PropertyType, type BookingMode,
  type HebergementCategory, type CategoryInput,
} from '@/lib/hebergement';
import { uploadMediaFile, MEDIA_LIMITS } from '@/hooks/useUpload';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface MediaItem { url: string; type: string }

interface SortableMediaTileProps {
  id: string;
  item: MediaItem;
  isCover: boolean;
  onCover: () => void;
  onRemove: () => void;
}

/** Vignette de galerie déplaçable (l'ordre est enregistré comme ordre d'affichage). */
function SortableMediaTile({ id, item, isCover, onCover, onRemove }: SortableMediaTileProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-lg overflow-hidden border bg-background-100 group ${isDragging ? 'border-primary-400 ring-2 ring-primary-200/60' : 'border-background-200/70'}`}
    >
      {item.type === 'video' ? (
        <div className="w-full h-full flex items-center justify-center text-foreground-400"><i className="ri-video-line text-2xl"></i></div>
      ) : (
        <img src={item.url} alt="" className="w-full h-full object-cover" />
      )}
      {isCover && (
        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-[9px] font-semibold">Couverture</span>
      )}
      <button
        type="button"
        {...attributes}
        {...listeners}
        title="Glisser pour réordonner"
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"
      >
        <i className="ri-draggable text-[11px] text-foreground-700"></i>
      </button>
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-1 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity">
        {item.type === 'image' && !isCover ? (
          <button type="button" onClick={onCover} title="Définir en couverture"
            className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center cursor-pointer">
            <i className="ri-star-line text-[11px] text-foreground-700"></i>
          </button>
        ) : <span />}
        <button type="button" onClick={onRemove} title="Retirer"
          className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center cursor-pointer">
          <i className="ri-delete-bin-line text-[11px] text-red-600"></i>
        </button>
      </div>
    </div>
  );
}

interface Props {
  initial: HebergementProperty | null;
  initialMedia?: { url: string; type: string; caption?: string | null }[];
  initialAmenities?: string[];
  currency: string;
  categories: HebergementCategory[];
  onCreateCategory: (input: CategoryInput) => Promise<HebergementCategory | null>;
  onClose: () => void;
  onSubmit: (
    data: Partial<HebergementProperty>,
    media: { url: string; type: string }[],
    amenities: string[],
  ) => Promise<void>;
}

interface PaysRow { id: number; nom_pays: string; codepays: number }
interface VilleRow { id: number; id_pays: number; nom_ville: string }

// Supprime les doublons de villes (même nom) et trie par ordre alphabétique
function dedupeVilles(villes: VilleRow[]): VilleRow[] {
  const seen = new Set<string>();
  return villes
    .filter((v) => {
      const key = (v.nom_ville || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.nom_ville.localeCompare(b.nom_ville, 'fr'));
}

export default function PropertyFormModal({
  initial, initialMedia, initialAmenities, currency, categories, onCreateCategory, onClose, onSubmit,
}: Props) {
  const [name, setName] = useState(initial?.name || '');
  const [type, setType] = useState<string>(initial?.type || 'apartment');
  const [description, setDescription] = useState(initial?.description || '');
  const [address, setAddress] = useState(initial?.address || '');
  const [city, setCity] = useState(initial?.city || '');
  const [region, setRegion] = useState(initial?.region || '');
  const [country, setCountry] = useState(initial?.country || '');
  const [capacity, setCapacity] = useState(String(initial?.capacity ?? 2));
  const [bedCount, setBedCount] = useState(String(initial?.bed_count ?? 1));
  const [bathroomCount, setBathroomCount] = useState(String(initial?.bathroom_count ?? 1));
  const [price, setPrice] = useState(String(initial?.price ?? 0));
  const [bookingMode, setBookingMode] = useState<BookingMode>((initial?.booking_mode as BookingMode) || 'ENTIRE_PROPERTY');
  const [coverImage, setCoverImage] = useState(initial?.cover_image || '');
  const [published, setPublished] = useState(initial?.published ?? false);
  const [mediaList, setMediaList] = useState<MediaItem[]>(
    (initialMedia || []).map((m) => ({ url: m.url, type: m.type || 'image' })),
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const mediaIds = mediaList.map((m, i) => `${m.url}-${i}`);
  const handleMediaDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = mediaIds.indexOf(String(active.id));
    const newIndex = mediaIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    setMediaList((prev) => arrayMove(prev, oldIndex, newIndex));
  };
  const [amenities, setAmenities] = useState<string[]>(initialAmenities || []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Localisation depuis la base (pays / ville)
  const [paysList, setPaysList] = useState<PaysRow[]>([]);
  const [villeList, setVilleList] = useState<VilleRow[]>([]);

  // Création d'un type d'hébergement personnalisé
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);

  // Options de type : types de la base (table `typehebergement`), sinon repli
  // sur les types par défaut. Les sous-types sont affichés en retrait.
  const typeOptions: { value: string; label: string; icon: string; isChild: boolean }[] =
    categories.length > 0
      ? orderedCategories(categories).map(({ cat, isChild }) => ({
          value: cat.slug,
          label: cat.nom,
          icon: cat.icon || 'ri-building-4-line',
          isChild,
        }))
      : PROPERTY_TYPES.map((t) => ({ value: t.value, label: t.label, icon: t.icon, isChild: false }));

  // Chargement initial des pays + villes du pays sélectionné
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await supabase.from('pays').select('id, nom_pays, codepays').order('nom_pays');
        if (!mounted) return;
        const list = (data || []) as PaysRow[];
        setPaysList(list);
        const initialCountry = initial?.country || '';
        if (initialCountry) {
          const p = list.find((x) => x.nom_pays === initialCountry);
          if (p) {
            const { data: villes } = await supabase
              .from('ville')
              .select('id, id_pays, nom_ville')
              .eq('id_pays', p.codepays)
              .order('nom_ville');
            if (mounted) setVilleList(dedupeVilles((villes || []) as VilleRow[]));
          }
        }
      } catch {
        /* silencieux : la localisation reste facultative */
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCountryChange = async (paysNom: string) => {
    setCountry(paysNom);
    setCity('');
    const p = paysList.find((x) => x.nom_pays === paysNom);
    if (!p) {
      setVilleList([]);
      return;
    }
    try {
      const { data: villes } = await supabase
        .from('ville')
        .select('id, id_pays, nom_ville')
        .eq('id_pays', p.codepays)
        .order('nom_ville');
      setVilleList(dedupeVilles((villes || []) as VilleRow[]));
    } catch {
      setVilleList([]);
    }
  };

  const createType = async () => {
    const label = newCatLabel.trim();
    if (!label) return;
    setCreatingCat(true);
    try {
      const created = await onCreateCategory({ nom: label });
      if (created) {
        setType(created.slug);
        setNewCatLabel('');
        setNewCatOpen(false);
      }
    } catch {
      setUploadError("Impossible de créer le type d'hébergement.");
    } finally {
      setCreatingCat(false);
    }
  };

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadError('');
    setUploading(true);
    try {
      const added: { url: string; type: string }[] = [];
      for (const file of Array.from(files)) {
        const kind = file.type.startsWith('video/') ? 'video' : 'image';
        const url = await uploadMediaFile(file, 'hebergement');
        added.push({ url, type: kind });
      }
      setMediaList((prev) => [...prev, ...added]);
      if (!coverImage && added.length > 0 && added[0].type === 'image') setCoverImage(added[0].url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Erreur lors de l\u2019envoi du fichier.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeMedia = (index: number) => {
    setMediaList((prev) => prev.filter((_, i) => i !== index));
  };

  const makeCover = (url: string) => setCoverImage(url);

  const toggleAmenity = (name: string) => {
    setAmenities((prev) => (prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name]));
  };

  const submit = async () => {
    setError('');
    if (!name.trim()) {
      setError('Le nom du bien est obligatoire.');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        type: type as PropertyType,
        description: description.trim() || null,
        address: address.trim() || null,
        city: city.trim() || null,
        region: region.trim() || null,
        country: country.trim() || null,
        capacity: Math.max(1, parseInt(capacity, 10) || 1),
        bed_count: Math.max(1, parseInt(bedCount, 10) || 1),
        bathroom_count: Math.max(1, parseInt(bathroomCount, 10) || 1),
        price: parseFloat(price) || 0,
        booking_mode: bookingMode,
        cover_image: coverImage.trim() || mediaList[0]?.url || null,
        published,
        status: published ? 'active' : 'draft',
      }, mediaList, amenities);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\u2019enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const selectClass = 'w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer';

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full sm:max-w-2xl bg-background-50 sm:rounded-2xl rounded-t-2xl border border-background-200/70 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground-950">
            {initial ? 'Modifier le bien' : 'Nouveau bien d\u2019hébergement'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && (
            <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
              <span className="text-xs text-red-700">{error}</span>
            </div>
          )}

          {/* Type d'hébergement */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-foreground-600">Type d&apos;hébergement</label>
              <button
                type="button"
                onClick={() => setNewCatOpen((v) => !v)}
                className="text-[11px] font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-circle-line"></i>Nouveau type
              </button>
            </div>
            {newCatOpen && (
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={newCatLabel}
                  onChange={(e) => setNewCatLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void createType(); } }}
                  placeholder="Ex. Chalet, Bungalow, Ryad…"
                  className="flex-1 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                />
                <button
                  type="button"
                  onClick={createType}
                  disabled={creatingCat || !newCatLabel.trim()}
                  className="px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50"
                >
                  {creatingCat ? <i className="ri-loader-4-line animate-spin"></i> : 'Créer'}
                </button>
              </div>
            )}
            <div className="relative">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={selectClass}
              >
                {typeOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.isChild ? '\u00a0\u00a0\u2014 ' : ''}{t.label}
                  </option>
                ))}
                {type && !typeOptions.some((t) => t.value === type) && (
                  <option value={type}>{type}</option>
                )}
              </select>
              <i
                className={`${
                  typeOptions.find((t) => t.value === type)?.icon || 'ri-building-4-line'
                } absolute right-8 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-sm`}
              ></i>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom du bien *</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ex. Villa Dar Atlas"
              className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={3} maxLength={2000}
              placeholder="Décrivez le bien, son ambiance, ses atouts…"
              className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 resize-none"
            />
          </div>

          {/* Localisation (pays / ville depuis la base) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Adresse</label>
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="N° et rue"
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Pays</label>
              <select value={country} onChange={(e) => handleCountryChange(e.target.value)} className={selectClass}>
                <option value="">Sélectionnez un pays</option>
                {paysList.map((p) => <option key={p.id} value={p.nom_pays}>{p.nom_pays}</option>)}
                {country && !paysList.some((p) => p.nom_pays === country) && <option value={country}>{country}</option>}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Ville</label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={!country}
                className={`${selectClass} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <option value="">{country ? 'Sélectionnez une ville' : 'Choisissez un pays d\u2019abord'}</option>
                {villeList.map((v) => <option key={v.id} value={v.nom_ville}>{v.nom_ville}</option>)}
                {city && !villeList.some((v) => v.nom_ville === city) && <option value={city}>{city}</option>}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Région</label>
              <input type="text" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Ex. Marrakech-Safi"
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
            </div>
          </div>

          {/* Caractéristiques */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Capacité</label>
              <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Lits</label>
              <input type="number" min={1} value={bedCount} onChange={(e) => setBedCount(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Salles de bain</label>
              <input type="number" min={1} value={bathroomCount} onChange={(e) => setBathroomCount(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Prix / nuit ({currency})</label>
              <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
            </div>
          </div>
          {price && (
            <p className="text-xs text-foreground-500 -mt-2">Soit {formatMoney(parseFloat(price) || 0, currency)} la nuit.</p>
          )}

          {/* Galerie photos / vidéos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-foreground-600">Galerie (photos &amp; vidéos)</label>
              <span className="text-[11px] text-foreground-400">Glissez pour réordonner · Images ≤ {MEDIA_LIMITS.image.label}</span>
            </div>
            {uploadError && <p className="text-xs text-red-600 mb-2">{uploadError}</p>}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMediaDragEnd}>
              <SortableContext items={mediaIds} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {mediaList.map((m, i) => (
                    <SortableMediaTile
                      key={`${m.url}-${i}`}
                      id={`${m.url}-${i}`}
                      item={m}
                      isCover={coverImage === m.url}
                      onCover={() => makeCover(m.url)}
                      onRemove={() => removeMedia(i)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="aspect-square rounded-lg border-2 border-dashed border-background-300/70 flex flex-col items-center justify-center gap-1 text-foreground-400 hover:border-primary-400 hover:text-primary-500 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {uploading ? <i className="ri-loader-4-line animate-spin text-xl"></i> : <i className="ri-add-line text-xl"></i>}
                    <span className="text-[10px]">{uploading ? 'Envoi…' : 'Ajouter'}</span>
                  </button>
                </div>
              </SortableContext>
            </DndContext>
            <input
              ref={fileInputRef}
              type="file"
              accept={`${MEDIA_LIMITS.image.accept},${MEDIA_LIMITS.video.accept}`}
              multiple
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
            />
          </div>

          {/* Équipements */}
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-2">Équipements &amp; services</label>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_AMENITIES.map((a) => {
                const active = amenities.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleAmenity(a)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border ${
                      active ? 'bg-secondary-500 text-background-50 border-secondary-500' : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'
                    }`}
                  >
                    {active && <i className="ri-check-line mr-1"></i>}{a}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode de réservation */}
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-2">Comment se réserve ce bien ?</label>
            <div className="space-y-2">
              {BOOKING_MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setBookingMode(m.value)}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-lg border text-left transition-colors cursor-pointer ${
                    bookingMode === m.value
                      ? 'border-primary-400 bg-primary-50/60'
                      : 'border-background-200/70 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    bookingMode === m.value ? 'border-primary-500' : 'border-background-300'
                  }`}>
                    {bookingMode === m.value && <span className="w-2.5 h-2.5 rounded-full bg-primary-500"></span>}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-900">{m.label}</span>
                    <span className="block text-xs text-foreground-500 mt-0.5">{m.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Publication */}
          <button
            type="button"
            onClick={() => setPublished((v) => !v)}
            className="w-full flex items-center justify-between p-3.5 rounded-lg border border-background-200/70 bg-background-50 cursor-pointer"
          >
            <span className="flex items-center gap-2 text-sm text-foreground-800">
              <i className={published ? 'ri-global-line text-accent-600' : 'ri-eye-off-line text-foreground-400'}></i>
              Publier sur mon site Zifek
            </span>
            <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${published ? 'bg-accent-500' : 'bg-background-200'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${published ? 'translate-x-6' : 'translate-x-1'}`} />
            </span>
          </button>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 h-16 border-t border-background-200/70 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
            Annuler
          </button>
          <button
            onClick={submit} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving ? <><i className="ri-loader-4-line animate-spin"></i>Enregistrement…</> : <><i className="ri-check-line"></i>{initial ? 'Enregistrer' : 'Créer le bien'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}